import { useMemo } from "react";
import { useMutation } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import { useController, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { gql } from "__generated__/gql";
import {
  makeClearCachedType,
  makeClearListFieldCache,
} from "utils/apollo/cacheHelpers";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getDataIsByIdField } from "utils/apollo/misc";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { RenderContentProps } from "components/Shared/MergeModal/MergeModalButton";
import {
  GRID_LAYOUT_PROPS,
  LabeledValueRow,
} from "components/Shared/LabeledValueTable";
import Address from "components/Shared/Address";
import EmptyPlaceholder from "components/Shared/EmptyPlaceholder";
import { formatInteger } from "utils/formatting";
import { APP_NAME } from "utils/config";
import { adminRetailerPage, brandRetailerPage } from "utils/links";
import {
  AdminMergeRetailerMutation,
  RetailerFilter,
} from "__generated__/gql/graphql";
import { EmbeddedAdminManageRetailersTable } from "components/Admin/Retailers/Manage/Table";
import type { AppliedFilter } from "components/Shared/FilterEditor/utils";
import { INCLUDE_CUSTOM_FILTER_NAME } from "components/Shared/IncludeCustomFilter";
import { RETAILER_TYPE_FILTER_ID } from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import { IS_ARCHIVED_FILTER_NAME } from "components/Shared/ArchivedStatusButtonGroup";
import RetailerDetails from "./Details";
import RetailersTable from ".";

const query = gql(/* GraphQL */ `
  query AdminSelectedRetailerMerge($retailerId: UUID!) {
    retailer(id: $retailerId) {
      id
      isArchived
      isCustom
      forBrandId
      retailerType
      ...RetailerDetails
      retailerLocationsList(
        orderBy: CREATED_AT_ASC
        filter: { isUnknown: { equalTo: false } }
      ) {
        id
        name
        address {
          id
          ...AddressComponentFields
        }
      }
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation AdminMergeRetailer($fromId: UUID!, $toId: UUID!) {
    mergeRetailers(input: { fromRetailerId: $fromId, toRetailerId: $toId }) {
      retailer {
        id
      }
    }
  }
`);

type FormFields = {
  toId: UUID;
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  toId: yup
    .string()
    .uuid()
    .nullable()
    // eslint-disable-next-line no-template-curly-in-string
    .required("Please select a ${label} with which to merge this retailer.")
    .label("Destination Retailer"),
});

const { ControlledFormField } = createFormFieldComponents<FormFields>({
  schema,
});

type Props = RenderContentProps & {
  isAdmin?: boolean;
  onCompleted: (data: AdminMergeRetailerMutation) => void;
  retailerId: UUID;
};

export default function MergeRetailer({
  formId,
  isAdmin,
  onClose,
  onCompleted,
  retailerId,
  setIsLoading,
}: Props) {
  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: { toId: "" },
  });

  const brandContext = useActiveBrandIdContext();

  const context = isAdmin ? ADMIN_CONTEXT : brandContext;

  const { data, loading } = useQuery(query, {
    context,
    variables: { retailerId },
    returnPartialData: true,
    nextFetchPolicy: "standby",
  });

  const [performMutation] = useMutation(mutation, {
    context,
    update: (cache) => {
      const clearCachedType = makeClearCachedType(cache);
      cache.modify({
        fields: makeClearListFieldCache("retailers"),
      });
      clearCachedType("Retailer");
      cache.gc();
    },
    onQueryUpdated: (observableQuery) => {
      const queryData = observableQuery.getLastResult()?.data as {
        [key: string]: unknown;
      };
      if (getDataIsByIdField(queryData, "retailer", retailerId)) {
        return false;
      }
    },
    onCompleted: (completedData) => {
      onClose();
      onCompleted(completedData);
    },
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) => {
      const { toId } = formData;
      setIsLoading(true);
      return performMutation({
        variables: { fromId: retailerId, toId },
      }).finally(() => setIsLoading(false));
    },
    { successMessage: "Retailer merged successfully" },
  );

  const toIdController = useController({
    control: formContext.control,
    name: "toId",
  });

  const { retailer } = data || {};

  const retailerFilters = useMemo<RetailerFilter>(() => {
    const baseFilter: RetailerFilter = { id: { notEqualTo: retailerId } };

    if (isAdmin) {
      if (retailer?.forBrandId) {
        baseFilter.or = [
          { forBrandId: { equalTo: retailer.forBrandId } },
          { forBrandId: { isNull: true } },
        ];
      } else {
        baseFilter.isCustom = { equalTo: false };
      }
    } else {
      baseFilter.isCustom = { equalTo: true };
    }

    return baseFilter;
  }, [isAdmin, retailerId, retailer]);

  const defaultFilters = useMemo(() => {
    const result: AppliedFilter[] = [];

    if (!retailer?.isArchived) {
      result.push({
        filter: IS_ARCHIVED_FILTER_NAME,
        value: false,
        variant: "equalTo",
      });
    }

    if (isAdmin) {
      result.push({
        filter: INCLUDE_CUSTOM_FILTER_NAME,
        value: !!retailer?.isCustom,
        variant: "equalTo",
      });

      if (retailer?.retailerType) {
        result.push({
          filter: RETAILER_TYPE_FILTER_ID,
          value: [retailer.retailerType],
          variant: "in",
        });
      }
    }

    if (!result.length) return;

    return result;
  }, [isAdmin, retailer]);

  return (
    <>
      <Box fontSize="sm">
        <Text>
          This will attempt to merge all of the attributes and associations of
          this selected retailer into another retailer. Attributes that have
          already been set on the destination retailer will take precedence.
          This action cannot be undone and should be used with{" "}
          <strong>extreme caution</strong> as retailer merges cause numerous
          downstream changes.
        </Text>
        <Text my={3}>
          In some cases, when both the selected retailer and the destination
          retailer have been added to{" "}
          {isAdmin ? `${APP_NAME} accounts` : "this brand"},{" "}
          <strong>the merge may fail</strong>. In this case, please review each
          usage of the retailer
          {isAdmin ? " in each brand account using it" : ""} and delete one if
          possible.
        </Text>
        <Text my={3}>
          Once merged, this currently selected retailer will be removed:
        </Text>

        <RetailerDetails
          data={data?.retailer}
          getRetailerLink={isAdmin ? adminRetailerPage : brandRetailerPage}
          isAdmin={isAdmin}
          isLoading={loading}
          isNameIncluded
          {...GRID_LAYOUT_PROPS}
        >
          <LabeledValueRow
            label="Locations"
            value={
              retailer?.retailerLocationsList?.length ? (
                <List maxH="15em" overflowY="auto">
                  {retailer.retailerLocationsList.map((rl, idx) => (
                    <ListItem
                      key={rl.id}
                      my={2}
                      _first={{ mt: 0 }}
                      _last={{ mb: 0 }}
                    >
                      {rl.name || (
                        <EmptyPlaceholder>
                          Location {formatInteger(idx)}
                        </EmptyPlaceholder>
                      )}
                      <Address
                        address={rl.address}
                        fontSize=".9em"
                        fontWeight={300}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : null
            }
          />
        </RetailerDetails>

        {isAdmin && retailer?.isCustom && (
          <Alert my={10}>
            <AlertIcon />
            <AlertDescription lineHeight="normal">
              This is a <strong>custom</strong> retailer. If you choose to merge
              it into a <strong>default</strong> retailer, the brand that
              created it will no longer be able to edit its details (though it
              will still be able to edit its placement details, which is usually
              all that is needed).
            </AlertDescription>
          </Alert>
        )}
      </Box>

      <Heading as="h6" size="xs" color="magenta.veryDark" mt={6}>
        Select Destination Retailer
      </Heading>
      <Text>
        Select the retailer into which you would like to merge this retailer:
      </Text>

      <Form
        formContext={formContext}
        onValidSubmit={handleValidSubmit}
        id={formId}
        mt={4}
      >
        <ControlledFormField
          name="toId"
          label={null}
          renderInput={({ field }) => <input type="hidden" {...field} />}
        />
      </Form>

      {isAdmin ? (
        <EmbeddedAdminManageRetailersTable
          defaultFilters={defaultFilters}
          defaultPageSize={10}
          filter={retailerFilters}
          nextFetchPolicy="standby"
          selectedRetailerId={toIdController.field.value}
          onSelectedRetailerIdChange={(id) => toIdController.field.onChange(id)}
        />
      ) : (
        <RetailersTable
          defaultFilters={defaultFilters}
          filter={retailerFilters}
          nextFetchPolicy="standby"
          selected={toIdController.field.value}
          onSelectedChange={(id) => toIdController.field.onChange(id)}
        />
      )}
    </>
  );
}
